import { useEffect, useCallback, useRef, useMemo } from "react";
import { useAppStore } from "@/core/store/useAppStore";
import { apiClient } from "@/services/apiClient";
import { API_CONFIG } from "@/config/api";

// Module-level mutex for token refresh to prevent concurrent refreshes
let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

export const useTokenValidation = () => {
  const { user, setUser, logout: storeLogout } = useAppStore();
  const isLoggingOut = useRef(false);

  const logout = useCallback(() => {
    if (isLoggingOut.current) return;

    isLoggingOut.current = true;

    storeLogout();

    setTimeout(() => {
      isLoggingOut.current = false;
    }, 1000);
  }, [storeLogout]);

  const refreshToken = useCallback(async (): Promise<boolean> => {
    if (isLoggingOut.current || !user?.refreshToken) return false;

    // If already refreshing, wait for the existing refresh to complete
    if (isRefreshing && refreshPromise) {
      return refreshPromise;
    }

    isRefreshing = true;
    refreshPromise = (async () => {
      try {
        const response = await fetch(`${API_CONFIG.URL}/auth/refresh`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.refreshToken}`,
          },
        });

        if (response.ok) {
          const data = await response.json();

          // Calculate new expiration from JWT
          let newExpiresAt = user.expiresAt;
          try {
            if (data.accessToken) {
              const parts = data.accessToken.split(".");
              if (parts.length === 3) {
                const payload = JSON.parse(atob(parts[1]));
                if (payload.exp) {
                  newExpiresAt = payload.exp * 1000;
                }
              }
            }
          } catch {
            newExpiresAt = Date.now() + 3600 * 1000;
          }

          const updatedUser = {
            ...user,
            accessToken: data.accessToken,
            refreshToken: data.refreshToken || user.refreshToken,
            expiresAt: newExpiresAt,
          };

          setUser(updatedUser);
          // persist middleware handles localStorage
          return true;
        } else {
          console.warn("⚠️ Token refresh failed");
          logout();
          return false;
        }
      } catch (error) {
        console.warn("⚠️ Token refresh error:", error);
        logout();
        return false;
      } finally {
        isRefreshing = false;
        refreshPromise = null;
      }
    })();

    return refreshPromise;
  }, [user, setUser, logout]);

  const validateToken = useCallback(
    async (token: string) => {
      if (isLoggingOut.current) return false;

      try {
        await apiClient.get("/health");
        return true;
      } catch (error: any) {
        console.warn("⚠️ Token validation failed:", error.message);

        if (error.response?.status === 401 || error.response?.status === 403) {
          const refreshed = await refreshToken();
          if (!refreshed) {
            logout();
          }
          return refreshed;
        }
        return true;
      }
    },
    [logout, refreshToken],
  );

  // Auto-refresh token before expiration and periodic validation
  useEffect(() => {
    if (!user?.accessToken || isLoggingOut.current) return;

    // Set up periodic token validation and proactive refresh (every 15 minutes)
    const interval = setInterval(
      async () => {
        if (isLoggingOut.current) return;

        // Use store state directly instead of localStorage
        const currentUser = useAppStore.getState().user;
        if (!currentUser?.accessToken) return;

        if (currentUser.expiresAt) {
          const timeUntilExpiry = currentUser.expiresAt - Date.now();

          // If token expires in less than 10 minutes, refresh it proactively
          if (timeUntilExpiry < 10 * 60 * 1000) {
            await refreshToken();
          } else {
            await validateToken(currentUser.accessToken);
          }
        } else {
          await validateToken(currentUser.accessToken);
        }
      },
      15 * 60 * 1000,
    );

    return () => clearInterval(interval);
  }, [user?.accessToken, validateToken, refreshToken]);

  // Activity-based refresh - refresh on user activity when token is near expiry
  useEffect(() => {
    if (!user?.accessToken || isLoggingOut.current) return;

    const handleActivity = async () => {
      if (isLoggingOut.current) return;

      // Use store state directly instead of localStorage
      const currentUser = useAppStore.getState().user;
      if (!currentUser?.accessToken) return;

      if (currentUser.expiresAt) {
        const timeUntilExpiry = currentUser.expiresAt - Date.now();

        // If token expires in less than 5 minutes, refresh it on activity
        if (timeUntilExpiry < 5 * 60 * 1000) {
          await refreshToken();
        }
      }
    };

    const events = ["click", "keydown", "scroll", "mousemove"];
    let activityTimeout: ReturnType<typeof setTimeout>;

    const throttledActivity = () => {
      clearTimeout(activityTimeout);
      activityTimeout = setTimeout(handleActivity, 1000);
    };

    events.forEach((event) => {
      document.addEventListener(event, throttledActivity, { passive: true });
    });

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, throttledActivity);
      });
      clearTimeout(activityTimeout);
    };
  }, [user?.accessToken, refreshToken]);

  const isTokenExpired = useCallback((): boolean => {
    if (!user) return true;

    // First check if we have stored expiration timestamp (for Google OAuth tokens)
    if (user.expiresAt) {
      return Date.now() >= user.expiresAt;
    }

    // Fallback: try to decode as JWT (for backend tokens)
    try {
      const parts = user.accessToken.split(".");
      if (parts.length === 3) {
        const tokenPayload = JSON.parse(atob(parts[1]));
        if (tokenPayload.exp) {
          const expirationTime = tokenPayload.exp * 1000;
          return Date.now() >= expirationTime;
        }
      }
    } catch {
      // Can't decode, assume valid
    }

    // No expiration info available, assume valid
    return false;
  }, [user]);

  const isAuthenticated = useMemo(() => {
    if (!user?.accessToken) return false;
    return !isTokenExpired();
  }, [user, isTokenExpired]);

  // Auto-refresh/logout when token is detected as expired
  useEffect(() => {
    if (user?.accessToken && isTokenExpired() && !isLoggingOut.current) {
      console.warn("⚠️ Token expired, attempting immediate refresh");
      refreshToken();
    }
  }, [user, isTokenExpired, refreshToken]);

  return {
    isAuthenticated,
    user,
    logout,
    validateToken,
    refreshToken,
  };
};

